import {Component, OnDestroy, OnInit} from "@angular/core";
import {BaseComponent} from "../../base.component";
import {LoggerService} from "../../common/logger.service";
import {UserService} from "../../common/user.service";
import {AoUpsellService} from "./ao-upsell.service";
import {Subscription} from "rxjs";
import {AutoOrder, IShippingCycleType} from "../../autoorder/auto-order-models";
import * as _ from "lodash";
import {ICart, IProductLine} from "../../cart/cart-models";
import {CartService} from "../../cart/cart.service";
import {TranslateService} from "../../translation/translate.service";
import {AoUpsellUtil} from "./ao-upsell-util";
import {Util} from "../../util/util";
import {AoUtil} from "../../autoorder/auto-order-util";
import {AutoOrderCacheService} from "../ao-cache.service";
import {SHIPPING_CYCLE_TYPE_4, SHIPPING_CYCLE_TYPE_8} from "../../constants";

@Component({
    selector: 'ao-upsell',
    templateUrl: './ao-upsell.component.html',
    styleUrls: ['./ao-upsell.component.scss'],
    standalone: false
})
export class AoUpsellComponent extends BaseComponent implements OnInit, OnDestroy {

    subscription = new Subscription();
    showAoUpsell: boolean = false;
    createAoUpsell: boolean = false;
    cart: ICart;
    autoOrders: AutoOrder[];
    isEditingCycle: boolean = false;
    selectedShippingCycleType: IShippingCycleType;
    selectedShippingCycleTypeMsg: string;
    shippingCycleTypes: IShippingCycleType[] = [];
    aoUpsellSubtotal: number;
    totalIoReward: number;

    constructor(
        loggerService: LoggerService,
        userService: UserService,
        private cartService: CartService,
        private aoCacheService: AutoOrderCacheService,
        private aoUpsellService: AoUpsellService,
        private translateService: TranslateService,
    ) {
        super(userService, loggerService);
    }

    ngOnInit(): void {
        this.getCustomerData().then(() => {
            if (this.allowAoUpsellCart) {
                this.createAoUpsell = this.aoUpsellService.aoUpsellSubject.isPresent();
                this.setAutoOrderCycleTypes();

                this.subscription.add(this.cartService.cart$.subscribe((cart: ICart) => {
                    if (cart) {
                        this.cart = cart;
                        this.getAoUpsell();
                    }
                }));

                this.subscription.add(this.aoCacheService.autoOrdersSubject.subscribe((autoOrders: AutoOrder[]) => {
                    if (autoOrders) {
                        this.autoOrders = autoOrders;
                        this.getAoUpsell();
                    }
                }));

                this.subscription.add(this.aoUpsellService.currentAoUpsellProducts.subscribe((productLines) => {
                    this.aoUpsellSubtotal = this.sumSubtotal(productLines);
                }));

                if (this.allowIor) {
                    this.subscription.add(this.aoUpsellService.totalIoReward.subscribe((ior) => {
                        this.totalIoReward = ior;
                    }));
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    getAoUpsell() {
        if (!!this.cart && !!this.autoOrders) {
            this.showAoUpsell = this.allowAoUpsellCart && this.autoOrders.length < this.maximumAutoOrders && AoUpsellUtil.filterAoProducts(this.cart.products).length > 0;
            this.setCreateAoUpsell();
        }
    }

    setCreateAoUpsell() {
        if (this.showAoUpsell) {
            if (this.createAoUpsell) {
                const filteredCartProducts: IProductLine[] = AoUpsellUtil.filterAoProducts(_.cloneDeep(this.cart.products));
                if (this.aoUpsellService.aoUpsellSubject.isPresent()) {
                    // only update the products that already exist in the ao upsell list
                    const products: IProductLine[] = _.filter(filteredCartProducts, (cartProduct: IProductLine) => !!AoUpsellUtil.getProductInputByLine(cartProduct, this.aoUpsellService.aoUpsellSubject.get().products));
                    this.aoUpsellService.aoUpsellSubject.set({products: AoUtil.convertToProductInputs(products), shippingCycleWeeks: this.selectedShippingCycleType?.weeks});
                } else {
                    this.aoUpsellService.aoUpsellSubject.set({products: AoUtil.convertToProductInputs(filteredCartProducts), shippingCycleWeeks: this.selectedShippingCycleType?.weeks});
                }
            } else {
                this.aoUpsellService.aoUpsellSubject.clear();
            }
            this.aoUpsellService.aoUpsellLoadedSubject.set(true);
        } else {
            this.aoUpsellService.aoUpsellSubject.clear();
        }
    }

    setAutoOrderCycleTypes(): void {
        this.shippingCycleTypes = [SHIPPING_CYCLE_TYPE_4, SHIPPING_CYCLE_TYPE_8];

        _.forEach(this.shippingCycleTypes, (ct: IShippingCycleType) => {
            this.translateService.translate(ct.tag).then((data: string) => ct.description = data, (error: any) => {
                this.log.error("translate autoorder cycle types", error);
                ct.description = ct.tag;
            });
        });

        if (this.aoUpsellService.aoUpsellSubject.get()?.shippingCycleWeeks) {
            this.selectedShippingCycleType = _.find(this.shippingCycleTypes, (ct: IShippingCycleType) => ct.weeks === this.aoUpsellService.aoUpsellSubject.get().shippingCycleWeeks);
        }
        if (!this.selectedShippingCycleType) {
            this.selectedShippingCycleType = SHIPPING_CYCLE_TYPE_4; // default to 4 weeks
        }
        this.setSelectedShippingCycleTypeMsg();
    }

    onShippingCycleTypeChange() {
        this.isEditingCycle = false;
        this.setSelectedShippingCycleTypeMsg();
        this.setCreateAoUpsell();
    }

    setSelectedShippingCycleTypeMsg() {
        this.translateService.translateRequest({tag: 'cart.checkout.ao.current.cycle.type', replacements: new Map([['count', this.selectedShippingCycleType.weeks]])}).then((data: string) => {
            this.selectedShippingCycleTypeMsg = data;
        });
    }

    sumSubtotal(productLines: IProductLine[]): number {
        return Util.safeRound(_.sumBy(productLines, (productLine: IProductLine) => {
            const itemPrice: number = productLine.product.aoPrice?.price ?? productLine.product.price.price;
            return itemPrice * productLine.quantity;
        }));
    }
}
